import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CircleDot, Crosshair, KeyRound, Loader2, Square, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useConfirm } from "@/components/ConfirmDialog";
import { api, formatApiError, wsUrl } from "@/lib/api";

const VIEW_W = 1280;
const VIEW_H = 800;
const SPECIAL_KEYS = [
  "Enter", "Tab", "Backspace", "Delete", "Escape",
  "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
  "Home", "End", "PageUp", "PageDown",
];

const ACTION_COLORS = {
  goto: "text-[#000000]",
  click: "text-black",
  fill: "text-[#000000]",
  press: "text-[#374151]",
  assert_text: "text-[#000000]",
  assert_visible: "text-[#000000]",
};

export default function RecorderPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const name = params.get("name") || "Untitled test";
  const targetUrl = params.get("url") || "";
  const authStateId = params.get("authStateId") || "";
  const projectId = params.get("projectId") || "";
  const suiteId = params.get("suiteId") || "";
  const envId = params.get("envId") || "";

  const [status, setStatus] = useState("starting"); // starting | live | reconnecting | disconnected | error | saving
  const [errorMsg, setErrorMsg] = useState("");
  const [frame, setFrame] = useState(null);
  const [steps, setSteps] = useState([]);
  const [currentUrl, setCurrentUrl] = useState(targetUrl);
  const [urlInput, setUrlInput] = useState(targetUrl);
  const [assertMode, setAssertMode] = useState(false);
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [authName, setAuthName] = useState("");
  const [savingAuth, setSavingAuth] = useState(false);

  const sessionRef = useRef(null);
  const testIdRef = useRef(null);
  const wsRef = useRef(null);
  const startedRef = useRef(false);
  const finishedRef = useRef(false);
  const discardingRef = useRef(false);
  const deletedIdsRef = useRef(new Set());
  const viewRef = useRef(null);
  const wheelAcc = useRef({ dx: 0, dy: 0, timer: null });
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef(null);
  const autosaveTimer = useRef(null);
  const [autosaveState, setAutosaveState] = useState("idle"); // idle | saving | saved | error

  const connectWs = (sessionId, wsPath) => {
    const ws = new WebSocket(wsUrl(wsPath));
    wsRef.current = ws;
    ws.onopen = () => {
      reconnectAttempts.current = 0;
      setStatus("live");
    };
    ws.onmessage = (ev) => {
      const msg = JSON.parse(ev.data);
      if (msg.type === "frame") setFrame(msg.data);
      else if (msg.type === "step") setSteps((p) => [...p, msg.step]);
      else if (msg.type === "step_updated")
        setSteps((p) => p.map((s) => (s.id === msg.step.id ? msg.step : s)));
      else if (msg.type === "nav") {
        setCurrentUrl(msg.url);
        setUrlInput(msg.url);
      } else if (msg.type === "init") {
        setSteps(msg.steps);
        setCurrentUrl(msg.url);
        setUrlInput(msg.url);
      } else if (msg.type === "ping") {
        try { ws.send(JSON.stringify({ type: "pong" })); } catch (_) {}
      }
    };
    ws.onclose = (ev) => {
      if (finishedRef.current) return;
      // Backend explicitly rejects (4404) when session is gone — stop trying
      if (ev.code === 4404) {
        setStatus("disconnected");
        return;
      }
      if (reconnectAttempts.current >= 8) {
        setStatus("disconnected");
        return;
      }
      reconnectAttempts.current += 1;
      setStatus("reconnecting");
      const delay = Math.min(1000 * reconnectAttempts.current, 4000);
      reconnectTimer.current = setTimeout(() => connectWs(sessionId, wsPath), delay);
    };
    ws.onerror = () => { /* onclose will follow */ };
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (!targetUrl) {
      setStatus("error");
      setErrorMsg("No target URL provided");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/recorder/sessions", {
          target_url: targetUrl,
          auth_state_id: authStateId || null,
        });
        sessionRef.current = data.session_id;
        // Create the test case immediately so steps are autosaved as we go.
        try {
          const { data: test } = await api.post("/tests", {
            name,
            target_url: targetUrl,
            auth_state_id: authStateId || null,
            project_id: projectId || null,
            suite_id: suiteId || null,
            default_environment_id: envId || null,
            steps: [],
          });
          testIdRef.current = test.id;
        } catch (e) {
          // Not fatal — recorder can still run; final save happens on Finish.
          console.warn("Auto-save test creation failed:", e);
        }
        connectWs(data.session_id, data.ws_path);
      } catch (e) {
        setStatus("error");
        setErrorMsg(formatApiError(e));
      }
    })();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
      if (sessionRef.current && !finishedRef.current) {
        api.post(`/recorder/sessions/${sessionRef.current}/stop`).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave: whenever the steps list changes, debounce-save to the backend.
  useEffect(() => {
    if (!testIdRef.current || finishedRef.current || discardingRef.current) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      const kept = steps.filter((s) => !deletedIdsRef.current.has(s.id));
      setAutosaveState("saving");
      api
        .put(`/tests/${testIdRef.current}`, { steps: kept })
        .then(() => setAutosaveState("saved"))
        .catch(() => setAutosaveState("error"));
    }, 700);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [steps]);

  const send = (msg) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify(msg));
  };

  const handleViewClick = (e) => {
    const el = viewRef.current;
    if (!el || !frame) return;
    el.focus();
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * VIEW_W;
    const y = ((e.clientY - rect.top) / rect.height) * VIEW_H;
    send({ type: "click", x, y, assertMode });
    if (assertMode) setAssertMode(false);
  };

  const handleKeyDown = (e) => {
    if (e.metaKey || e.ctrlKey) return;
    if (e.key.length === 1) {
      send({ type: "type", text: e.key });
      e.preventDefault();
    } else if (SPECIAL_KEYS.includes(e.key)) {
      send({ type: "key", key: e.key });
      e.preventDefault();
    }
  };

  // Forward the OS clipboard contents to the recorded page as a single
  // `type` burst so the page sees real keystrokes (and our DOM input
  // listener fires). Without this, Cmd+V silently drops the text and the
  // user's recording ends up with an empty `fill` step.
  const handlePaste = (e) => {
    const text = e.clipboardData?.getData("text");
    if (text) {
      send({ type: "type", text });
      e.preventDefault();
    }
  };

  const handleWheel = (e) => {
    const acc = wheelAcc.current;
    acc.dx += e.deltaX;
    acc.dy += e.deltaY;
    if (!acc.timer) {
      acc.timer = setTimeout(() => {
        send({ type: "wheel", dx: acc.dx, dy: acc.dy });
        acc.dx = 0;
        acc.dy = 0;
        acc.timer = null;
      }, 80);
    }
  };

  const handleGoto = () => {
    if (urlInput.trim()) send({ type: "goto", url: urlInput.trim() });
  };

  const deleteStep = (id) => {
    deletedIdsRef.current.add(id);
    setSteps((p) => p.filter((s) => s.id !== id));
  };

  const handleSaveAuth = async () => {
    if (!authName.trim()) return;
    setSavingAuth(true);
    try {
      await api.post(`/recorder/sessions/${sessionRef.current}/save-auth`, { name: authName.trim() });
      toast.success(`Login state "${authName.trim()}" saved. Attach it to tests to skip login.`);
      setAuthDialogOpen(false);
      setAuthName("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingAuth(false);
    }
  };

  const handleFinish = async () => {
    if (!sessionRef.current) return;
    setStatus("saving");
    finishedRef.current = true;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    try {
      send({ type: "flush" });
      await new Promise((r) => setTimeout(r, 600));
      // Pass the test_id so the backend can flush buffered record-time
      // snapshots (captured live during the session) into the
      // `step_snapshots` collection. Guarantees every newly-recorded
      // test has a baseline snapshot for diff diagnostics, even if its
      // very first run fails.
      const stopParams = testIdRef.current ? { test_id: testIdRef.current } : undefined;
      const { data } = await api.post(
        `/recorder/sessions/${sessionRef.current}/stop`,
        null,
        stopParams ? { params: stopParams } : undefined,
      );
      const kept = data.steps.filter((s) => !deletedIdsRef.current.has(s.id));
      let testId = testIdRef.current;
      if (testId) {
        await api.put(`/tests/${testId}`, {
          name,
          target_url: targetUrl,
          auth_state_id: authStateId || null,
          steps: kept,
        });
      } else {
        // Fallback if upfront create failed
        const { data: test } = await api.post("/tests", {
          name,
          target_url: targetUrl,
          auth_state_id: authStateId || null,
          project_id: projectId || null,
          suite_id: suiteId || null,
          default_environment_id: envId || null,
          steps: kept,
        });
        testId = test.id;
      }
      toast.success("Recording saved as test case");
      navigate(`/tests/${testId}`);
    } catch (e) {
      toast.error(formatApiError(e));
      setStatus("live");
      finishedRef.current = false;
    }
  };

  const handleDiscard = async () => {
    const ok = await confirm({
      title: "Discard this recording?",
      description: "The live browser session will end and the captured steps will be thrown away. You'll need to start a new recording from scratch.",
      confirmLabel: "Discard",
      danger: true,
      testId: "confirm-discard-recording",
    });
    if (!ok) return;
    discardingRef.current = true;
    finishedRef.current = true;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    if (sessionRef.current) api.post(`/recorder/sessions/${sessionRef.current}/stop`).catch(() => {});
    if (testIdRef.current) {
      try { await api.delete(`/tests/${testIdRef.current}`); } catch (_) { /* ignore */ }
    }
    navigate(projectId ? `/projects/${projectId}/tests` : "/projects");
  };

  return (
    <div className="h-screen flex flex-col bg-[#F8FAFC]" data-testid="recorder-page">
      {/* Toolbar */}
      <div className="bg-white border-b border-[#E5E7EB] px-3 sm:px-4 py-2.5 flex flex-wrap items-center gap-2 sm:gap-3 shadow-sm">
        <div className="flex items-center gap-2 mr-2">
          <span className="relative flex h-2.5 w-2.5">
            {status === "live" && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EF4444] opacity-75" />}
            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${status === "live" ? "bg-[#EF4444]" : "bg-[#94A3B8]"}`} />
          </span>
          <span className="text-xs font-semibold text-[#0F172A]" data-testid="recorder-status">
            {status === "live" ? "Recording" : status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
          {testIdRef.current && (
            <span className="ml-1 text-[10px] uppercase tracking-wider text-[#64748B] hidden sm:inline" data-testid="autosave-indicator">
              {autosaveState === "saving" ? "· Saving…" :
                autosaveState === "saved" ? "· Auto-saved" :
                autosaveState === "error" ? "· Save failed" : ""}
            </span>
          )}
        </div>
        <div className="flex-1 flex items-center gap-1 min-w-[200px]">
          <Input
            data-testid="recorder-url-input"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleGoto()}
            className="rounded-md border-[#E5E7EB] h-9 font-mono text-xs focus-visible:ring-[#3B82F6] focus-visible:border-[#3B82F6]"
          />
          <button onClick={handleGoto} data-testid="recorder-goto-button" className="flex items-center justify-center h-9 w-9 rounded-md border border-[#E5E7EB] bg-white hover:bg-[#F8FAFC] text-[#475569] transition-colors">
            <ArrowRight size={13} />
          </button>
        </div>
        <button
          onClick={() => setAssertMode((v) => !v)}
          data-testid="assert-mode-toggle"
          className={`flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border transition-colors ${
            assertMode
              ? "bg-[#3B82F6] text-white border-[#3B82F6] hover:bg-[#2563EB]"
              : "bg-white border-[#E5E7EB] text-[#0F172A] hover:bg-[#F8FAFC]"
          }`}
          title="Assert mode: next click records an assertion instead of clicking"
        >
          <Crosshair size={13} />
          {assertMode ? "Click element to assert" : "Assert"}
        </button>
        <button
          onClick={() => setAuthDialogOpen(true)}
          data-testid="save-auth-state-button"
          className="flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border border-[#E5E7EB] bg-white text-[#0F172A] hover:bg-[#F8FAFC] transition-colors"
          title="Save current cookies/localStorage as a reusable login state"
        >
          <KeyRound size={13} />
          Save login state
        </button>
        <button
          onClick={handleDiscard}
          data-testid="discard-recording-button"
          className="flex items-center gap-1.5 h-9 px-3 text-sm font-medium rounded-md border border-[#E5E7EB] text-[#B91C1C] bg-white hover:bg-[#FEF2F2] hover:border-[#FCA5A5] transition-colors"
        >
          <X size={13} />
          Discard
        </button>
        <button
          onClick={handleFinish}
          disabled={status === "saving"}
          data-testid="finish-recording-button"
          className="flex items-center gap-1.5 h-9 px-4 text-sm font-semibold rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] transition-colors disabled:opacity-60 shadow-sm"
        >
          {status === "saving" ? <Loader2 size={13} className="animate-spin" /> : <Square size={11} fill="currentColor" />}
          Finish &amp; save
        </button>
      </div>

      <div className="flex-1 flex flex-col md:flex-row min-h-0">
        {/* Live view */}
        <div className="flex-1 bg-[#0F172A] flex items-center justify-center p-3 min-w-0 overflow-hidden min-h-[280px]">
          {status === "error" ? (
            <div className="text-center text-white" data-testid="recorder-error">
              <p className="text-sm font-medium text-[#FCA5A5]">Session failed</p>
              <p className="text-sm mt-2 text-white/70 max-w-md">{errorMsg}</p>
            </div>
          ) : !frame ? (
            <div className="text-center text-white/70 text-sm flex items-center gap-2" data-testid="recorder-loading">
              <Loader2 size={15} className="animate-spin" />
              Launching browser…
            </div>
          ) : (
            <div
              ref={viewRef}
              tabIndex={0}
              onClick={handleViewClick}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onWheel={handleWheel}
              data-testid="recorder-live-view"
              className={`outline-none max-w-full max-h-full ${assertMode ? "cursor-crosshair ring-2 ring-[#3B82F6]" : "cursor-pointer"}`}
              style={{ aspectRatio: `${VIEW_W}/${VIEW_H}` }}
            >
              <img
                src={`data:image/jpeg;base64,${frame}`}
                alt="Live browser"
                className="max-w-full max-h-[calc(100vh-140px)] select-none rounded-lg shadow-2xl"
                draggable={false}
              />
            </div>
          )}
        </div>

        {/* Steps panel */}
        <div className="w-full md:w-[340px] md:max-w-[340px] bg-white border-t md:border-t-0 md:border-l border-[#E5E7EB] flex flex-col max-h-[40vh] md:max-h-none">
          <div className="px-4 py-3 border-b border-[#F1F5F9]">
            <span className="text-xs font-medium text-[#64748B]">Recorded steps</span>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="font-['Geist'] font-semibold text-2xl text-[#0F172A]" data-testid="step-count">{steps.length}</span>
              <span className="text-xs text-[#64748B] truncate">{name}</span>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="recorded-steps-list">
            {steps.length === 0 && (
              <p className="text-xs text-[#64748B] p-4 leading-relaxed">
                Interact with the live browser. Clicks, typing and navigation are captured automatically.
              </p>
            )}
            {steps.map((s, i) => (
              <div key={s.id} className="flex items-start gap-2 px-3 py-2.5 border-b border-[#F1F5F9] group hover:bg-[#F8FAFC]" data-testid={`recorded-step-${i}`}>
                <span className="font-mono text-[10px] text-[#94A3B8] mt-0.5 w-5">{String(i + 1).padStart(2, "0")}</span>
                <CircleDot size={12} className={`mt-0.5 shrink-0 ${ACTION_COLORS[s.action] || "text-[#64748B]"}`} />
                <div className="min-w-0 flex-1">
                  <span className={`text-[10px] font-semibold uppercase tracking-wide ${ACTION_COLORS[s.action] || "text-[#64748B]"}`}>{s.action}</span>
                  <p className="text-xs text-[#334155] truncate" title={s.description}>{s.description}</p>
                  {s.selector && <p className="font-mono text-[10px] text-[#94A3B8] truncate" title={s.selector}>{s.selector}</p>}
                </div>
                <button
                  onClick={() => deleteStep(s.id)}
                  data-testid={`delete-recorded-step-${i}`}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded text-[#B91C1C] hover:bg-[#FEF2F2] transition-all"
                >
                  <Trash2 size={11} />
                </button>
              </div>
            ))}
          </div>
          <div className="px-4 py-3 border-t border-[#F1F5F9] bg-[#F8FAFC]">
            <p className="text-[11px] text-[#64748B] leading-relaxed">
              <strong className="text-[#334155]">Tip:</strong> Log into your app here, then click <em>Save login state</em> — future runs skip the login (your password is never stored).
            </p>
          </div>
        </div>
      </div>

      {/* Save auth state dialog */}
      <Dialog open={authDialogOpen} onOpenChange={setAuthDialogOpen}>
        <DialogContent className="sm:max-w-[420px] rounded-xl border-[#E5E7EB]">
          <DialogHeader>
            <DialogTitle className="font-['Geist'] font-semibold tracking-tight text-[#0F172A]">Save login state</DialogTitle>
            <DialogDescription className="text-sm text-[#64748B]">
              Saves the browser&apos;s current cookies and localStorage (encrypted). Attach it to tests so runs start already logged in.
            </DialogDescription>
          </DialogHeader>
          <Input
            data-testid="auth-state-name-input"
            value={authName}
            onChange={(e) => setAuthName(e.target.value)}
            placeholder='e.g. "Staging admin session"'
            className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setAuthDialogOpen(false)} className="px-4 h-9 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white text-[#0F172A] hover:bg-[#F8FAFC]">
              Cancel
            </button>
            <button
              onClick={handleSaveAuth}
              disabled={savingAuth || !authName.trim()}
              data-testid="confirm-save-auth-button"
              className="px-4 h-9 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60 flex items-center gap-2"
            >
              {savingAuth && <Loader2 size={13} className="animate-spin" />}
              Save
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
